import React, { ComponentType } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

const client = new QueryClient();

/**
 * Higher-order component that provides a QueryClient instance to the wrapped component.
 *
 * @template Props - The props type of the wrapped component.
 * @param WrappedComponent - The component to be wrapped.
 * @param withDevtools - Whether to include React Query Devtools.
 * @returns The wrapped component with QueryClientProvider.
 */

const withQueryClient =
    <Props extends object>(WrappedComponent: ComponentType<Props>, withDevtools = true) =>
    (props: Props) => {
        try {
            useQueryClient();
            return <WrappedComponent {...props} />;
        } catch (error) {
            if ((error as Error).message.match(/(QueryClient|QueryClientProvider)/i)) {
                return (
                    <QueryClientProvider client={client}>
                        <WrappedComponent {...props} />
                        {withDevtools && process.env.NODE_ENV !== 'production' && (
                            <ReactQueryDevtools initialIsOpen={false} />
                        )}
                    </QueryClientProvider>
                );
            }
            throw error;
        }
    };

export default withQueryClient;
